import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link, TextField, Typography } from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuthStore } from '@/store/authStore';
import AuthShell from '@/components/auth/AuthShell';
import OtpInput from '@/components/auth/OtpInput';
import { redirectTarget } from '@/utils/auth';

const DEFAULT_ERROR =
  "Identifiant ou mot de passe incorrect. Si le problème persiste, contactez le support : hello@runnrz.app";

const apiError = (err: any, fallback: string): string =>
  err?.response?.data?.errors?.[0] || err?.response?.data?.error || (err?.response ? fallback : err?.message) || fallback;

const Login: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const prefilledEmail = (location.state as { email?: string } | null)?.email || '';
  const notice = (location.state as { notice?: string } | null)?.notice || '';

  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');

  const createSession = useAuthStore((state) => state.createSession);
  const completeTwoFactorSession = useAuthStore((state) => state.completeTwoFactorSession);
  const target = redirectTarget(location.state);

  const handleSubmitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      const result = await createSession(email.trim(), password);
      if (result.status === 'need_two_factor') {
        setChallenge(result.challenge);
        setOtpCode('');
        setStep('otp');
      } else {
        navigate(target, { replace: true });
      }
    } catch (err: any) {
      setError(apiError(err, DEFAULT_ERROR));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (code: string) => {
    if (!challenge || code.length !== 6 || loading) return;
    setLoading(true);
    setError('');
    try {
      await completeTwoFactorSession(challenge, code);
      navigate(target, { replace: true });
    } catch (err: any) {
      setError(apiError(err, 'Code invalide ou expiré. Saisissez le nouveau code affiché par votre application.'));
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  const goBackToCredentials = () => {
    setStep('credentials');
    setChallenge(null);
    setOtpCode('');
    setError('');
  };

  const detectCapsLock = (e: React.KeyboardEvent) => setCapsLock(e.getModifierState?.('CapsLock') ?? false);

  return (
    <AuthShell
      steps={[
        { key: 'credentials', label: 'Identifiants' },
        { key: 'otp', label: 'Code 2FA' },
      ]}
      activeStep={step === 'credentials' ? 0 : 1}
    >
      {step === 'credentials' ? (
        <Box component="form" onSubmit={handleSubmitCredentials} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, letterSpacing: '-0.02em' }}>
              Connexion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Utilisez votre compte d'équipe.
            </Typography>
          </Box>

          {notice && !error && <Alert severity="success">{notice}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Adresse e-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus={!prefilledEmail}
            required
            fullWidth
          />

          <Box>
            <TextField
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={detectCapsLock}
              onKeyDown={detectCapsLock}
              autoComplete="current-password"
              autoFocus={!!prefilledEmail}
              required
              fullWidth
              helperText={capsLock ? 'Verrouillage majuscules activé' : undefined}
              FormHelperTextProps={{ sx: { color: 'warning.main' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
              <Link component={RouterLink} to="/set-password" state={{ email }} variant="body2" underline="hover">
                Mot de passe oublié ?
              </Link>
            </Box>
          </Box>

          <Button type="submit" variant="contained" size="large" disabled={loading || !email || !password} sx={{ height: 46 }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Continuer'}
          </Button>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            verifyOtp(otpCode);
          }}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}
        >
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, letterSpacing: '-0.02em' }}>
              Vérification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Saisissez le code à 6 chiffres affiché par votre application d'authentification pour{' '}
              <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
                {email}
              </Box>
              .
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <OtpInput label="Code à 6 chiffres" value={otpCode} onChange={setOtpCode} onComplete={verifyOtp} disabled={loading} error={!!error} />

          <Button type="submit" variant="contained" size="large" disabled={loading || otpCode.length !== 6} sx={{ height: 46 }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Se connecter'}
          </Button>
          <Button variant="text" color="inherit" startIcon={<ArrowBack fontSize="small" />} onClick={goBackToCredentials} sx={{ alignSelf: 'center' }}>
            Changer de compte
          </Button>
        </Box>
      )}
    </AuthShell>
  );
};

export default Login;
